import { AxiosError } from 'axios'

import { COMPASS_API_ERRORS } from '@/components/flows/CompassFlow/constants'
import { API_VIOLATION_CODE } from '@/lib/constants'
import { isConstraintViolationListError } from '@/lib/helpers/helperFunctions'
import errorTypeService from '@/lib/services/errorTypeService'
import { ConstraintViolation } from '@/types/api'
import { FormErrors } from '@/types/misc'

// The API reports a rejected upload (wrong file type, too large, a
// password-protected PDF) as a violation against the file property of whichever
// entity holds the file. No form field is bound to those properties, so the
// message has to be shown by hand wherever we upload a file.
const fileViolationPropertyPaths = ['file', 'attachmentFile']

class ConstraintViolationService {
  getViolationByCode(
    error: unknown,
    code: string,
  ): ConstraintViolation | undefined {
    if (!isConstraintViolationListError(error)) {
      return
    }

    return error.response?.data?.violations.find(
      (violation) => violation.code === code,
    )
  }

  getViolationByPropertyPath(
    error: unknown,
    propertyPath: string,
  ): ConstraintViolation | undefined {
    if (!isConstraintViolationListError(error)) {
      return
    }

    return error.response?.data?.violations.find(
      (violation) => violation.propertyPath === propertyPath,
    )
  }

  /**
   * The message for a file the API refused to accept, e.g. a password-protected
   * PDF, or null if the error isn't about an uploaded file.
   */
  getFileViolationMessage(error: unknown): string | null {
    if (!isConstraintViolationListError(error)) {
      return null
    }

    const violations = error.response?.data?.violations || []

    const fileViolation = violations.find((violation) =>
      fileViolationPropertyPaths.includes(violation.propertyPath),
    )

    return fileViolation?.message || null
  }

  formatErrors(error: unknown): FormErrors {
    const isAxiosError = error instanceof AxiosError
    if (!isAxiosError) {
      throw new Error('Not a ConstraintViolationListError')
    }

    const violations = error?.response?.data?.violations
    if (!Array.isArray(violations)) {
      throw new Error('Not a ConstraintViolationListError')
    }

    const formattedErrors: FormErrors = {}

    violations.forEach((violation: ConstraintViolation) => {
      formattedErrors[violation.propertyPath] = this.formatError(violation)
    })

    return formattedErrors
  }

  formatError(error: ConstraintViolation): string {
    const { message, code } = error

    if (/This collection should contain 1 element or more/i.test(message)) {
      return 'Please select at least one option'
    }

    if (code === API_VIOLATION_CODE.invalidEnumValue) {
      return 'Please select a valid option'
    }

    if (message === COMPASS_API_ERRORS.required) {
      return 'This field is required'
    }

    if (message === COMPASS_API_ERRORS.invalidFloat) {
      return 'Must be a numeric value'
    }

    if (message === COMPASS_API_ERRORS.invalidInteger) {
      return 'Must be a number'
    }

    return message
  }

  /**
   * Every violation's message, for showing an API rejection where there's no
   * form field to put it against. Empty if the error isn't a violation list.
   */
  getViolationMessages(error: unknown): string[] {
    if (!isConstraintViolationListError(error)) {
      return []
    }

    const violations = error.response?.data?.violations || []

    return violations.map((violation) => this.formatError(violation))
  }

  hasViolationWithCode(error: unknown, violationCode: string): boolean {
    if (!errorTypeService.isConstraintViolationListError(error)) {
      return false
    }

    const violations = error.response?.data.violations || []

    return violations.some((violation) => violation.code === violationCode)
  }
}

const constraintViolationService = new ConstraintViolationService()

export default constraintViolationService
